import { Fragment, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useBulkUpload, getFundamentalName, getFundamentalColor,
  getBehaviorName, getHabitName, availableHabitsFor,
} from '@/api/recognition';
import type { FundamentalDto, DailyHabitDto, AttitudeBehaviorDto } from '@/api/recognition';
import { toRequestRows, applyValidationResult } from './rowUtils';
import { translateRowError, bulkRequestErrorMessage } from './errorMessages';
import type { BulkEditableRow, BulkRowStatus } from './types';

interface Props {
  rows: BulkEditableRow[];
  campaignId: string | null;
  /** Programa del wizard: el backend valida el lote con SUS reglas, no con las de Fundamentales. */
  programa?: string | null;
  fundamentals: FundamentalDto[];
  onRowsChange: (rows: BulkEditableRow[]) => void;
  onBack: () => void;
  onNext: () => void;
}

/** Espera tras la última edición antes de revalidar contra el backend. */
const REVALIDATE_DEBOUNCE_MS = 350;

/** Descarga el CSV de errores tal cual lo devuelve el backend (`linea,receptor,codigo,mensaje`). */
function downloadErrorsCsv(errorsCsv: string): void {
  // BOM explícito — mismo motivo que StepInstructions.tsx: los mensajes de error vienen en español
  // con acentos, y sin BOM Excel en Windows los muestra con la codepage del sistema, no UTF-8.
  const blob = new Blob(['﻿' + errorsCsv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = 'reconocimientos-errores.csv';
  anchor.click();
  URL.revokeObjectURL(url);
}

/** `row.habitIds` guarda los códigos de habit unidos por `';'` (no array) — se parsea aquí una sola vez. */
function parseHabitCodes(habitIds: string): string[] {
  return habitIds ? habitIds.split(';').map((code) => code.trim()) : [];
}

function toggleHabitCode(selectedCodes: string[], code: string): string[] {
  return selectedCodes.includes(code)
    ? selectedCodes.filter((selectedCode) => selectedCode !== code)
    : [...selectedCodes, code];
}

/**
 * Alterna un Fundamental dentro de `row.fundamentalIds` SIN perder entradas que el catálogo actual
 * no pudo resolver (código de un idioma/catálogo distinto): compara por id O código para decidir si
 * ya estaba marcado, y al desmarcar quita cualquiera de las dos representaciones — un `!==` directo
 * contra `fundamental.id` dejaría intacta (y duplicaría al volver a marcar) una entrada guardada por
 * código, que es justo como llegan las filas recién importadas del CSV.
 */
function toggleFundamentalId(rawIds: string[], fundamental: FundamentalDto): string[] {
  const matches = (id: string) => id === fundamental.id || id.toLowerCase() === fundamental.code.toLowerCase();
  return rawIds.some(matches) ? rawIds.filter((id) => !matches(id)) : [...rawIds, fundamental.id];
}

/**
 * Alterna un Comportamiento dentro de `row.behaviorIds`. A diferencia de
 * {@link toggleFundamentalId} (que agrega el `.id`), aquí se agrega el CÓDIGO — igual que
 * {@link toggleHabitCode} — porque el backend solo sabe resolver Comportamientos por código
 * (`BulkImportRecognitionsCommandHandler`, `behaviorsByFundamental` está indexado por código, sin el
 * doble soporte código-o-id que sí tiene la resolución de Fundamentales). Bug reportado por el
 * usuario (2026-08-23, misma tarde): marcar un Comportamiento por su `.id` aquí lo dejaba viajando
 * al backend como un valor que no existe, y la fila se rechazaba con "El Comportamiento no es válido"
 * justo al tocar la selección.
 */
function toggleBehaviorCode(rawIds: string[], behavior: AttitudeBehaviorDto): string[] {
  const matches = (id: string) => id === behavior.id || id.toLowerCase() === behavior.code.toLowerCase();
  return rawIds.some(matches) ? rawIds.filter((id) => !matches(id)) : [...rawIds, behavior.code];
}

/**
 * Recalcula qué Comportamientos/Daily Habits siguen siendo válidos tras cambiar los Fundamentales o
 * Comportamientos marcados en una fila, y descarta lo que quedó huérfano. Sin esto, desmarcar un
 * Fundamental (o un Comportamiento) dejaba su Comportamiento/Habits ya elegidos invisibles en la
 * tabla — el grupo correspondiente deja de pintarse — pero seguían viajando al backend en cada
 * revalidación, que los rechazaba con `behavior-invalid`/`habit-invalid` sin que hubiera manera de
 * ver por qué desde la pantalla (motivo del reporte del usuario: "se mezclan y no se muestra bien").
 * Mismo propósito que el `useEffect` de poda de `ReconocerPanel.tsx`, adaptado a esta tabla plana de
 * filas (sin estado propio por fila donde enganchar un efecto): se recalcula en el mismo evento que
 * dispara el cambio, no de forma reactiva.
 */
function pruneOrphanedSelections(
  nextFundamentalIds: string[],
  currentBehaviorIds: string[],
  currentHabitIds: string,
  allFundamentals: FundamentalDto[],
): { behaviorIds: string[]; habitIds: string } {
  const nextSelectedFundamentals = allFundamentals.filter((fundamental) =>
    nextFundamentalIds.some((id) => fundamental.id === id || fundamental.code.toLowerCase() === id.toLowerCase()));
  const nextAvailableBehaviors = nextSelectedFundamentals.flatMap((fundamental) => fundamental.attitudes ?? []);
  const prunedBehaviorIds = currentBehaviorIds.filter((rawId) =>
    nextAvailableBehaviors.some((behavior) => behavior.code === rawId || behavior.id === rawId));
  const resolvedBehaviorIds = nextAvailableBehaviors
    .filter((behavior) => prunedBehaviorIds.some((rawId) => behavior.code === rawId || behavior.id === rawId))
    .map((behavior) => behavior.id);
  const nextAvailableHabits = nextSelectedFundamentals.flatMap((fundamental) =>
    availableHabitsFor(fundamental, resolvedBehaviorIds));
  const prunedHabitIds = parseHabitCodes(currentHabitIds)
    .filter((code) => nextAvailableHabits.some((habit) => habit.code === code))
    .join(';');
  return { behaviorIds: prunedBehaviorIds, habitIds: prunedHabitIds };
}

interface HabitGroup {
  fundamental: FundamentalDto;
  color: string;
  habits: DailyHabitDto[];
}

/**
 * Agrupa los Daily Habits disponibles por Fundamental de origen. Con un solo Fundamental marcado
 * en la fila la lista plana anterior ya era clara, pero con 2+ no había forma de saber a cuál
 * pertenecía cada hábito — motivo del reporte del usuario. El color reutiliza la misma paleta que
 * ya usa `ReconocerPanel.tsx` (`getFundamentalColor`) para el mismo propósito en el alta individual.
 */
function groupHabitsByFundamental(
  selectedFundamentals: FundamentalDto[],
  allFundamentals: FundamentalDto[],
  selectedBehaviorIds: string[],
): HabitGroup[] {
  return selectedFundamentals
    .map((fundamental) => ({
      fundamental,
      color: getFundamentalColor(fundamental, allFundamentals.indexOf(fundamental)),
      habits: availableHabitsFor(fundamental, selectedBehaviorIds),
    }))
    .filter((group) => group.habits.length > 0);
}

interface BehaviorGroup {
  fundamental: FundamentalDto;
  color: string;
  behaviors: AttitudeBehaviorDto[];
}

/** Mismo propósito que {@link groupHabitsByFundamental}, para los Comportamientos disponibles. */
function groupBehaviorsByFundamental(
  selectedFundamentals: FundamentalDto[],
  allFundamentals: FundamentalDto[],
): BehaviorGroup[] {
  return selectedFundamentals
    .map((fundamental) => ({
      fundamental,
      color: getFundamentalColor(fundamental, allFundamentals.indexOf(fundamental)),
      behaviors: fundamental.attitudes ?? [],
    }))
    .filter((group) => group.behaviors.length > 0);
}

/**
 * Orden de la vista de revisión: las filas con error siempre arriba, para que sean lo primero que
 * se ve con lotes grandes (100+ filas) — pedido explícito del usuario. `Array.prototype.sort` es
 * estable desde ES2019, así que dentro de cada estado se conserva el orden original del archivo.
 */
const STATUS_RANK: Record<BulkRowStatus, number> = { invalid: 0, checking: 1, valid: 2 };

/** Texto compacto para la fila resumen: nombres unidos por coma, o `—` si no hay nada elegido. */
function summarizeOrDash(names: string[]): string {
  return names.length > 0 ? names.join(', ') : '—';
}

interface HabitToggleProps {
  isSelected: boolean;
  label: string;
  onToggle: () => void;
}

/**
 * Toggle de un Daily Habit dentro de la vista previa de carga masiva. Reemplaza el `<select
 * multiple>` nativo: en vertical (no píldoras horizontales) para que los nombres largos no se
 * corten, con check y fondo verde bien visibles cuando está seleccionado.
 */
function HabitToggle({ isSelected, label, onToggle }: Readonly<HabitToggleProps>) {
  return (
    <button type="button" className={`bulk-habit-toggle ${isSelected ? 'bulk-habit-toggle--active' : ''}`}
      aria-pressed={isSelected} onClick={onToggle}>
      <span className="bulk-habit-check">{isSelected && <i className="fas fa-check" />}</span>
      <span className="bulk-habit-label">{label}</span>
    </button>
  );
}

/** Paso 3 del wizard: preview automático al entrar + edición inline con re-validación. */
export function StepReviewRows({ rows, campaignId, programa, fundamentals, onRowsChange, onBack, onNext }: Readonly<Props>) {
  const { t, i18n } = useTranslation('recognition');
  const preview = useBulkUpload();
  const ranInitialPreview = useRef(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestSeqRef = useRef(0);
  // Vista compacta (REQ-106 seguimiento, 2026-08-23): con 100+ filas, el detalle completo de cada
  // una (Fundamental/Comportamiento/Habits/Mensaje) abierto de entrada volvía la tabla inmanejable.
  // Colapsado por defecto — se expande por fila bajo demanda; el resumen ya muestra lo esencial
  // (incluido el error, si lo hay) sin necesidad de abrir nada. Set por `lineNumber`, no por índice:
  // sobrevive a que las filas se reordenen (ver `STATUS_RANK`) sin desincronizarse.
  const [expandedLines, setExpandedLines] = useState<Set<number>>(new Set());
  const toggleExpanded = (lineNumber: number) => {
    setExpandedLines((prev) => {
      const next = new Set(prev);
      if (next.has(lineNumber)) next.delete(lineNumber); else next.add(lineNumber);
      return next;
    });
  };

  /**
   * Lanza el preview contra el backend. Cada llamada lleva un número de secuencia: si mientras
   * viaja la respuesta se dispara una edición más nueva, la respuesta vieja se descarta en vez de
   * pisar el estado más reciente.
   */
  const runPreview = (currentRows: BulkEditableRow[]) => {
    const seq = ++requestSeqRef.current;
    preview.mutate(
      { rows: toRequestRows(currentRows), campanaId: campaignId, preview: true, programa },
      { onSuccess: (result) => {
          if (seq !== requestSeqRef.current) return;
          onRowsChange(applyValidationResult(currentRows, result));
        } },
    );
  };

  useEffect(() => {
    if (ranInitialPreview.current) return;
    ranInitialPreview.current = true;
    runPreview(rows);
    // Solo se ejecuta una vez al entrar al paso — las re-validaciones posteriores las dispara la edición.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, []);

  /**
   * Actualiza el valor de una celda mientras se escribe, sin llamar al backend en cada tecla.
   * Devuelve el array ya actualizado (no depende del próximo render) para que `revalidate` pueda
   * usarlo de inmediato en el mismo evento — necesario para los toggles de Fundamental/Comportamiento/
   * Habit, que revalidan en el mismo `onClick` en vez de esperar a un `onBlur` que un botón nunca dispara.
   */
  const editField = (lineNumber: number, patch: Partial<BulkEditableRow>): BulkEditableRow[] => {
    const updated = rows.map((row) => (row.lineNumber === lineNumber ? { ...row, ...patch } : row));
    onRowsChange(updated);
    return updated;
  };

  /**
   * Revalida el lote completo a partir de un snapshot de filas ya actualizado, con debounce para
   * no lanzar una llamada por cada tecla o cambio consecutivo.
   */
  const revalidate = (currentRows: BulkEditableRow[]) => {
    const checking = currentRows.map((row) => ({ ...row, status: 'checking' as const }));
    onRowsChange(checking);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runPreview(checking), REVALIDATE_DEBOUNCE_MS);
  };

  const validCount = rows.filter((row) => row.status === 'valid').length;
  const invalidCount = rows.filter((row) => row.status === 'invalid').length;
  const sortedRows = [...rows].sort((rowA, rowB) => STATUS_RANK[rowA.status] - STATUS_RANK[rowB.status]);

  return (
    <div className="bulk-step bulk-step--review">
      <div className="bulk-summary">
        <span className="bulk-badge bulk-badge--ok"><i className="fas fa-check" /> {t('bulk.validRows', { count: validCount })}</span>
        {invalidCount > 0 && <span className="bulk-badge bulk-badge--err"><i className="fas fa-xmark" /> {t('bulk.invalidRows', { count: invalidCount })}</span>}
        {invalidCount > 0 && preview.data?.errorsCsv && (
          <button type="button" className="btn btn--secondary" onClick={() => downloadErrorsCsv(preview.data!.errorsCsv)}>
            <i className="fas fa-download" /> {t('bulk.downloadErrors')}
          </button>
        )}
      </div>
      <p className="rp-note">{t('bulk.reviewHint')}</p>
      {preview.isPending && <p role="status" className="bulk-status-pending">{t('bulk.revalidating')}</p>}
      {preview.isError && (
        <p className="rp-error" role="alert"><i className="fas fa-triangle-exclamation" /> {bulkRequestErrorMessage(t, preview.error)}</p>
      )}

      <div className="bulk-table-wrap">
        <table className="bulk-table">
          <thead>
            <tr>
              <th>{t('bulk.colLine')}</th>
              <th>{t('bulk.colReceptor')}</th>
              <th>{t('bulk.colFundamental')}</th>
              <th>{t('bulk.colBehavior')}</th>
              <th>{t('bulk.colHabits')}</th>
              <th>{t('bulk.colMensaje')}</th>
              <th>{t('bulk.colStatus')}</th>
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row) => {
              // El backend acepta el id del catálogo o el código corto (F01); se resuelve cada valor
              // guardado contra el catálogo para que las marcas correctas se vean activas aunque el
              // CSV traiga el código. Una fila puede tener MÁS de un Fundamental marcado — el tope de
              // cuántos se valida en el backend (por la OU del emisor de esta fila).
              const selectedFundamentals = fundamentals.filter((fundamental) =>
                row.fundamentalIds.some((id) =>
                  fundamental.id === id || fundamental.code.toLowerCase() === id.toLowerCase()));
              // `row.behaviorIds` guarda el CÓDIGO o el id (según venga del CSV o de un toggle previo),
              // igual que `row.fundamentalIds` — se resuelve contra el catálogo antes de poder filtrar
              // los Daily Habits por los Comportamientos elegidos en esta fila. Una fila puede tener
              // MÁS de un Comportamiento marcado, igual que el alta individual ya permite.
              const availableBehaviors = selectedFundamentals.flatMap((fundamental) => fundamental.attitudes ?? []);
              const selectedBehaviors = availableBehaviors.filter((behavior) =>
                row.behaviorIds.some((id) => behavior.code === id || behavior.id === id));
              // Mismo helper que ya usa el formulario normal de Reconocer (`ReconocerPanel.tsx`): si el
              // Fundamental tiene Comportamientos, solo muestra los Daily Habits de los elegidos — nunca
              // todos los del Fundamental, que es justo lo que confundía al usuario en la vista previa.
              const selectedBehaviorIds = selectedBehaviors.map((behavior) => behavior.id);
              const behaviorGroups = groupBehaviorsByFundamental(selectedFundamentals, fundamentals);
              const habitGroups = groupHabitsByFundamental(selectedFundamentals, fundamentals, selectedBehaviorIds);
              const habitCodes = parseHabitCodes(row.habitIds);
              const isExpanded = expandedLines.has(row.lineNumber);
              const errorText = row.status === 'invalid' ? translateRowError(t, row.errorCode, row.errorMessage) : '';
              return (
              <Fragment key={row.lineNumber}>
                <tr className={`bulk-row-summary ${row.status === 'invalid' ? 'bulk-row--err' : ''}`}>
                  <td>{row.lineNumber}</td>
                  <td>
                    <span className="bulk-cell-truncate" title={row.receptorEmail || row.receptorNomina}>
                      {row.receptorEmail || row.receptorNomina || '—'}
                    </span>
                  </td>
                  <td>
                    <span className="bulk-cell-truncate" title={selectedFundamentals.map((fundamental) => getFundamentalName(fundamental, i18n.language)).join(', ')}>
                      {summarizeOrDash(selectedFundamentals.map((fundamental) => getFundamentalName(fundamental, i18n.language)))}
                    </span>
                  </td>
                  <td>
                    <span className="bulk-cell-truncate" title={selectedBehaviors.map((behavior) => getBehaviorName(behavior, i18n.language)).join(', ')}>
                      {summarizeOrDash(selectedBehaviors.map((behavior) => getBehaviorName(behavior, i18n.language)))}
                    </span>
                  </td>
                  <td>{habitCodes.length > 0 ? t('bulk.habitsCount', { count: habitCodes.length }) : '—'}</td>
                  <td>
                    <span className="bulk-cell-truncate" title={row.mensaje}>{row.mensaje || '—'}</span>
                  </td>
                  <td>
                    <div className="bulk-status-summary">
                      {row.status === 'valid' && <span className="bulk-status-ok"><i className="fas fa-check" /> {t('bulk.ok')}</span>}
                      {row.status === 'invalid' && (
                        <span className="bulk-status-err bulk-cell-truncate" title={errorText}>{errorText}</span>
                      )}
                      {row.status === 'checking' && <span>…</span>}
                      <button type="button" className="bulk-expand-btn" aria-expanded={isExpanded}
                        aria-label={t(isExpanded ? 'bulk.collapseRow' : 'bulk.expandRow', { line: row.lineNumber })}
                        onClick={() => toggleExpanded(row.lineNumber)}>
                        <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`} />
                      </button>
                    </div>
                  </td>
                </tr>
                {isExpanded && (
                  <tr className="bulk-row-detail">
                    <td colSpan={7}>
                      <div className="bulk-detail-grid">
                        <div className="bulk-detail-field">
                          <label htmlFor={`receptor-${row.lineNumber}`}>{t('bulk.colReceptor')}</label>
                          <input id={`receptor-${row.lineNumber}`} value={row.receptorEmail} aria-label={t('bulk.colReceptor')}
                            onChange={(event) => editField(row.lineNumber, { receptorEmail: event.target.value })}
                            onBlur={() => revalidate(rows)} />
                          <input value={row.receptorNomina} aria-label={t('bulk.colReceptorNomina')}
                            placeholder={t('bulk.colReceptorNomina')}
                            onChange={(event) => editField(row.lineNumber, { receptorNomina: event.target.value })}
                            onBlur={() => revalidate(rows)} className="bulk-nomina-input" />
                        </div>
                        <div className="bulk-detail-field">
                          <label>{t('bulk.colFundamental')}</label>
                          <div className="bulk-habits-list" role="group" aria-label={t('bulk.colFundamental')}>
                            {fundamentals.map((fundamental) => (
                              <HabitToggle key={fundamental.id}
                                isSelected={selectedFundamentals.some((selected) => selected.id === fundamental.id)}
                                label={getFundamentalName(fundamental, i18n.language)}
                                onToggle={() => {
                                  const nextFundamentalIds = toggleFundamentalId(row.fundamentalIds, fundamental);
                                  const pruned = pruneOrphanedSelections(nextFundamentalIds, row.behaviorIds, row.habitIds, fundamentals);
                                  revalidate(editField(row.lineNumber, { fundamentalIds: nextFundamentalIds, ...pruned }));
                                }}
                              />
                            ))}
                          </div>
                        </div>
                        {behaviorGroups.length > 0 && (
                          <div className="bulk-detail-field">
                            <label>{t('bulk.colBehavior')}</label>
                            <div className="bulk-habits-list" role="group" aria-label={t('bulk.colBehavior')}>
                              {behaviorGroups.map((group) => (
                                <div key={group.fundamental.id} className="bulk-habits-group">
                                  {/* La etiqueta por Fundamental solo aparece con 2+ grupos — con uno solo
                                      repetiría el mismo nombre que ya se ve marcado en la columna Fundamental. */}
                                  {behaviorGroups.length > 1 && (
                                    <span className="bulk-habits-group-label" style={{ color: group.color, borderColor: group.color }}>
                                      {getFundamentalName(group.fundamental, i18n.language)}
                                    </span>
                                  )}
                                  {group.behaviors.map((behavior) => (
                                    <HabitToggle key={behavior.id}
                                      isSelected={selectedBehaviorIds.includes(behavior.id)}
                                      label={getBehaviorName(behavior, i18n.language)}
                                      onToggle={() => {
                                        const nextBehaviorIds = toggleBehaviorCode(row.behaviorIds, behavior);
                                        const pruned = pruneOrphanedSelections(row.fundamentalIds, nextBehaviorIds, row.habitIds, fundamentals);
                                        revalidate(editField(row.lineNumber, pruned));
                                      }}
                                    />
                                  ))}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {habitGroups.length > 0 && (
                          <div className="bulk-detail-field">
                            <label>{t('bulk.colHabits')}</label>
                            <div className="bulk-habits-list" role="group" aria-label={t('bulk.colHabits')}>
                              {habitGroups.map((group) => (
                                <div key={group.fundamental.id} className="bulk-habits-group">
                                  {/* La etiqueta por Fundamental solo aparece con 2+ grupos — con uno solo
                                      repetiría el mismo nombre que ya se ve marcado en la columna Fundamental. */}
                                  {habitGroups.length > 1 && (
                                    <span className="bulk-habits-group-label" style={{ color: group.color, borderColor: group.color }}>
                                      {getFundamentalName(group.fundamental, i18n.language)}
                                    </span>
                                  )}
                                  {group.habits.map((habit) => (
                                    <HabitToggle key={habit.id}
                                      isSelected={habitCodes.includes(habit.code)}
                                      label={getHabitName(habit, i18n.language)}
                                      onToggle={() => revalidate(editField(row.lineNumber, {
                                        habitIds: toggleHabitCode(habitCodes, habit.code).join(';'),
                                      }))}
                                    />
                                  ))}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        <div className="bulk-detail-field bulk-detail-field--wide">
                          <label htmlFor={`mensaje-${row.lineNumber}`}>{t('bulk.colMensaje')}</label>
                          <textarea id={`mensaje-${row.lineNumber}`} className="bulk-mensaje-input" value={row.mensaje} rows={3}
                            aria-label={t('bulk.colMensaje')}
                            onChange={(event) => editField(row.lineNumber, { mensaje: event.target.value })}
                            onBlur={() => revalidate(rows)} />
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="bulk-wizard-nav">
        <button type="button" className="btn btn--secondary" onClick={onBack}>
          <i className="fas fa-arrow-left" /> {t('bulk.back')}
        </button>
        <button type="button" className="btn btn--send" disabled={validCount === 0 || preview.isPending} onClick={onNext}>
          {t('bulk.next')} <i className="fas fa-arrow-right" />
        </button>
      </div>
    </div>
  );
}

export default StepReviewRows;
